import React, { useEffect, useState } from 'react';
import { Store, Plus, LogIn, Mail, Copy, Users, KeyRound, Stethoscope, Trash2 } from 'lucide-react';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { PageHead, Card, Th, Td, fmtDate, Pager } from './os2ui';

/* Admin — Restaurant OS instances: create, invite, enter as customer. */

const EMPTY_PROVISION = { company_name: '', owner_name: '', owner_email: '', founding: true, primary_color: '', logo_url: '', website: '', cuisine: '' };

function ProvisionCard({ isEs, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY_PROVISION);
  const [locs, setLocs] = useState([{ label: '', city: '' }]);
  const [mgrs, setMgrs] = useState([]);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setLoc = (i, k) => (e) => setLocs(locs.map((l, li) => (li === i ? { ...l, [k]: e.target.value } : l)));
  const inputCls = 'rounded-lg border border-gray-200 px-3 py-2 text-[13px]';

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const locations = locs.filter((l) => l.label.trim() || l.city.trim())
      .map((l) => ({ label: l.label.trim() || l.city.trim() || 'Main', city: l.city.trim() }));
    const r = await authFetch('/admin/restaurants/provision', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...f, locations: locations.length ? locations : [{ label: 'Main', city: '' }],
        managers: mgrs.filter((m) => m.email.trim()).map((m) => ({ email: m.email.trim(), name: m.name.trim() })) }),
    });
    const j = await r.json(); setBusy(false);
    if (!r.ok) { setErr(typeof j.detail === 'string' ? j.detail : 'error'); return; }
    setRes(j); onDone();
  };

  return (
    <Card className="mt-4 p-5" data-testid="radmin-provision-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[14px] font-bold text-gray-900">🚀 {isEs ? 'Invitar restaurante' : 'Invite Restaurant'}</p>
          <p className="text-[12px] text-gray-400">{isEs ? 'Aprovisiona el Restaurant AI OS completo con un clic — el owner solo crea su contraseña.' : 'Provision the full Restaurant AI OS in one click — the owner just sets a password.'}</p>
        </div>
        {!open && !res && (
          <button onClick={() => setOpen(true)} data-testid="radmin-provision-open"
            className="rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-5 py-2 transition-colors">
            {isEs ? 'Nueva invitación →' : 'New invitation →'}
          </button>
        )}
      </div>
      {open && !res && (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="radmin-provision-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input required value={f.company_name} onChange={set('company_name')} placeholder={isEs ? 'Restaurante / Empresa *' : 'Restaurant / Company *'} className={inputCls} data-testid="prov-company" />
            <input value={f.owner_name} onChange={set('owner_name')} placeholder={isEs ? 'Nombre del owner' : 'Owner name'} className={inputCls} data-testid="prov-owner" />
            <input required type="email" value={f.owner_email} onChange={set('owner_email')} placeholder="owner@email.com *" className={inputCls} data-testid="prov-email" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-1.5">{isEs ? 'Locations (cada una = su propio Restaurant AI OS)' : 'Locations (each one = its own Restaurant AI OS)'}</p>
            {locs.map((l, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={l.label} onChange={setLoc(i, 'label')} placeholder={isEs ? `Location ${i + 1} (ej. San Jose)` : `Location ${i + 1} (e.g. San Jose)`} className={`${inputCls} flex-1`} data-testid={`prov-loc-label-${i}`} />
                <input value={l.city} onChange={setLoc(i, 'city')} placeholder={isEs ? 'Ciudad, Estado' : 'City, State'} className={`${inputCls} flex-1`} data-testid={`prov-loc-city-${i}`} />
                {locs.length > 1 && <button type="button" onClick={() => setLocs(locs.filter((_, li) => li !== i))} className="text-gray-300 hover:text-red-500 px-1">✕</button>}
              </div>
            ))}
            <button type="button" onClick={() => setLocs([...locs, { label: '', city: '' }])} data-testid="prov-add-location"
              className="text-[12px] font-bold text-[#F77F11] hover:underline">+ {isEs ? 'Añadir location' : 'Add location'}</button>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-1.5">{isEs ? 'Managers (opcional — acceso a todas las locations)' : 'Managers (optional — access to all locations)'}</p>
            {mgrs.map((m, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input type="email" value={m.email} onChange={(e) => setMgrs(mgrs.map((x, xi) => (xi === i ? { ...x, email: e.target.value } : x)))} placeholder="manager@email.com" className={`${inputCls} flex-1`} data-testid={`prov-mgr-email-${i}`} />
                <input value={m.name} onChange={(e) => setMgrs(mgrs.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))} placeholder={isEs ? 'Nombre' : 'Name'} className={`${inputCls} w-40`} data-testid={`prov-mgr-name-${i}`} />
                <button type="button" onClick={() => setMgrs(mgrs.filter((_, xi) => xi !== i))} className="text-gray-300 hover:text-red-500 px-1">✕</button>
              </div>
            ))}
            <button type="button" onClick={() => setMgrs([...mgrs, { email: '', name: '' }])} data-testid="prov-add-manager"
              className="text-[12px] font-bold text-[#F77F11] hover:underline">+ {isEs ? 'Añadir manager' : 'Add manager'}</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={f.logo_url} onChange={set('logo_url')} placeholder="Logo URL (https:// o /tenants/…)" className={inputCls} data-testid="prov-logo" />
            <input value={f.primary_color} onChange={set('primary_color')} placeholder={isEs ? 'Color #hex (opcional)' : 'Color #hex (optional)'} className={inputCls} data-testid="prov-color" />
            <input value={f.website} onChange={set('website')} placeholder="Website (https://…)" className={inputCls} data-testid="prov-website" />
          </div>
          <label className="flex items-center gap-2 text-[13px] font-semibold text-amber-700 cursor-pointer">
            <input type="checkbox" checked={f.founding} onChange={(e) => setF({ ...f, founding: e.target.checked })} data-testid="prov-founding" />
            ⭐ Founding Restaurant — {isEs ? 'distinción permanente desde 2026' : 'permanent distinction since 2026'}
          </label>
          {err && <p className="text-[12px] text-red-600" data-testid="prov-error">{err}</p>}
          <div className="flex gap-2">
            <button disabled={busy} data-testid="prov-submit"
              className="rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white text-[13px] font-bold px-6 py-2 transition-colors disabled:opacity-50">
              {busy ? '…' : (isEs ? 'Aprovisionar y generar invitación' : 'Provision & generate invitation')}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-[12px] text-gray-400 hover:text-gray-700">{isEs ? 'Cancelar' : 'Cancel'}</button>
          </div>
        </form>
      )}
      {res && (
        <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="prov-result">
          <p className="text-[14px] font-bold text-emerald-800">✓ {res.company.name} — {isEs ? 'Your Restaurant AI OS is ready.' : 'Your Restaurant AI OS is ready.'}</p>
          <p className="mt-1 text-[12px] text-emerald-700">{(res.restaurants || []).map((x) => `${x.name} (${x.os_id})`).join(' · ')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="text-[11.5px] bg-white border border-emerald-200 rounded-lg px-3 py-1.5 break-all" data-testid="prov-invite-link">{res.invite_link}</code>
            <button onClick={() => { navigator.clipboard.writeText(res.invite_link); setCopied(true); setTimeout(() => setCopied(false), 2000); }} data-testid="prov-copy-link"
              className="rounded-full bg-emerald-600 text-white text-[12px] font-bold px-4 py-1.5 hover:bg-emerald-700 transition-colors">
              {copied ? '✓' : (isEs ? 'Copiar link' : 'Copy link')}
            </button>
            <button onClick={() => { setRes(null); setOpen(false); setF(EMPTY_PROVISION); setLocs([{ label: '', city: '' }]); setMgrs([]); }} className="text-[12px] text-gray-400 hover:text-gray-700">{isEs ? 'Cerrar' : 'Close'}</button>
          </div>
          {(res.manager_invites || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="prov-manager-invites">
              <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">{isEs ? 'Invitaciones de managers' : 'Manager invitations'}</p>
              {res.manager_invites.map((m) => (
                <div key={m.email} className="flex flex-wrap items-center gap-2 text-[11.5px]">
                  <span className="font-bold text-emerald-800">{m.email}</span>
                  <code className="bg-white border border-emerald-200 rounded px-2 py-1 break-all">{m.invite_link}</code>
                  <button onClick={() => navigator.clipboard.writeText(m.invite_link)} className="text-emerald-700 font-bold hover:underline">{isEs ? 'Copiar' : 'Copy'}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


export default function RestaurantsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [rows, setRows] = useState(null);
  const [connReqs, setConnReqs] = useState([]);
  const [accessReqs, setAccessReqs] = useState([]);
  const [connPage, setConnPage] = useState(1);
  const [accessPage, setAccessPage] = useState(1);
  const [name, setName] = useState('');
  const [msg, setMsg] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);
  const [invEmail, setInvEmail] = useState('');
  const [invName, setInvName] = useState('');
  const [invLink, setInvLink] = useState('');
  const [usersFor, setUsersFor] = useState(null);
  const [usersList, setUsersList] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [diag, setDiag] = useState(null);

  const openUsers = async (r) => {
    setUsersFor(r); setUsersList(null); setResetResult(null); setDiag(null);
    const resp = await authFetch(`/admin/restaurants/${r.id}`);
    if (resp.ok) { const j = await resp.json(); setUsersList(j.users || []); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(isEs ? `¿Generar nueva contraseña para ${u.email}? La actual dejará de funcionar.` : `Generate a new password for ${u.email}? The current one will stop working.`)) return;
    const resp = await authFetch(`/admin/restaurants/${usersFor.id}/users/${u.id}/reset-password`, { method: 'POST' });
    const j = await resp.json();
    if (resp.ok) { setResetResult(j); openUsersRefresh(); } else setResetResult({ error: typeof j.detail === 'string' ? j.detail : 'error' });
  };

  const openUsersRefresh = async () => {
    const resp = await authFetch(`/admin/restaurants/${usersFor.id}`);
    if (resp.ok) { const j = await resp.json(); setUsersList(j.users || []); }
  };

  const diagnose = async (u) => {
    setDiag({ loading: true });
    const resp = await authFetch('/admin/restaurants/diagnose', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: u.email }) });
    setDiag(resp.ok ? await resp.json() : { reasons: ['diagnose failed'] });
  };

  const removeInstance = async (r) => {
    const q1 = isEs ? `¿Eliminar la instancia "${r.name}" y TODOS sus datos (usuarios, órdenes, imports)? Esta acción no se puede deshacer.` : `Delete instance "${r.name}" and ALL its data (users, orders, imports)? This cannot be undone.`;
    if (!window.confirm(q1)) return;
    const typed = window.prompt(isEs ? `Escribe el nombre exacto para confirmar: ${r.name}` : `Type the exact name to confirm: ${r.name}`);
    if (typed !== r.name) return;
    const resp = await authFetch(`/admin/restaurants/${r.id}`, { method: 'DELETE' });
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok) { window.alert(typeof j.detail === 'string' ? j.detail : 'error'); return; }
    load();
  };


  const load = () => authFetch('/admin/restaurants').then((r) => r.json()).then((j) => setRows(j.restaurants)).catch(() => {});
  useEffect(() => {
    load();
    authFetch('/admin/restaurants/requests/connections').then((r) => r.json()).then((j) => { setConnReqs(j.requests || []); setAccessReqs(j.access_requests || []); }).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    const r = await authFetch('/admin/restaurants', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
    const j = await r.json();
    setMsg(r.ok ? { ok: true, text: `${j.name} · ${j.instance.os_id}` } : { ok: false, text: typeof j.detail === 'string' ? j.detail : 'error' });
    if (r.ok) { setName(''); load(); }
  };

  const invite = async (e) => {
    e.preventDefault();
    const r = await authFetch(`/admin/restaurants/${inviteFor.id}/invite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: invEmail, name: invName, role: 'owner' }) });
    const j = await r.json();
    if (r.ok) setInvLink(j.invite_link);
    else setInvLink(`ERROR: ${typeof j.detail === 'string' ? j.detail : 'failed'}`);
  };

  const enter = async (rid) => {
    const r = await authFetch(`/admin/restaurants/${rid}/enter`, { method: 'POST' });
    if (r.ok) window.location.href = '/restaurant';
  };

  return (
    <div data-testid="os2-restaurants">
      <div className="mb-4 inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="org-tabs">
        <a href="/admin/ai-os/restaurants" className="rounded-full px-4 py-1.5 text-[12px] font-bold bg-gray-900 text-white" data-testid="org-tab-org">{isEs ? 'Organización' : 'Organization'}</a>
        <a href="/admin/ai-os/users" className="rounded-full px-4 py-1.5 text-[12px] font-bold text-gray-500 hover:text-gray-900 transition-colors" data-testid="org-tab-identity">{isEs ? 'Identidad' : 'Identity'}</a>
      </div>
      <PageHead title={isEs ? 'Restaurantes (Restaurant AI OS)' : 'Restaurants (Restaurant AI OS)'}
        question={isEs ? 'Instancias privadas de clientes — crea, invita y entra como el cliente.' : 'Private customer instances — create, invite and enter as the customer.'} />

      <Card className="p-5">
        <form onSubmit={create} className="flex flex-wrap items-center gap-3">
          <Store className="w-4 h-4 text-[#F77F11]" />
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="radmin-new-name"
            placeholder={isEs ? 'Nombre del restaurante…' : 'Restaurant name…'}
            className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] w-64" />
          <button data-testid="radmin-create-btn" className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors">
            <Plus className="w-3.5 h-3.5" /> {isEs ? 'Crear instancia' : 'Create instance'}
          </button>
          {msg && <span className={`text-[12.5px] font-semibold ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`} data-testid="radmin-create-msg">{msg.ok ? '✓ ' : '✕ '}{msg.text}</span>}
        </form>
      </Card>

      <ProvisionCard isEs={isEs} onDone={load} />

      {connReqs.length > 0 && (
        <Card className="mt-4 p-5" data-testid="radmin-connection-requests">
          <p className="text-[12px] font-bold text-gray-900 mb-3">
            🔌 {isEs ? `Solicitudes de conexión pendientes (${connReqs.length})` : `Pending connection requests (${connReqs.length})`}
          </p>
          <div className="space-y-2">
            {connReqs.slice((connPage - 1) * 5, connPage * 5).map((q) => (
              <div key={`${q.restaurant_id}-${q.provider}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px]" data-testid={`radmin-conn-req-${q.provider}`}>
                <span className="font-bold text-gray-900">{q.restaurant_name || q.restaurant_id}</span>
                <span className="rounded-full bg-white border border-amber-300 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-700">{q.provider}</span>
                <span className="text-gray-500">{q.requested_by}</span>
                {q.note && <span className="text-gray-500 italic">“{q.note}”</span>}
                <span className="ml-auto text-[11px] text-gray-400">{fmtDate(q.requested_at)}</span>
              </div>
            ))}
          </div>
          <Pager page={connPage} pages={Math.max(1, Math.ceil(connReqs.length / 5))} setPage={setConnPage} testid="radmin-conn-pager" />
        </Card>
      )}

      {accessReqs.length > 0 && (
        <Card className="mt-4 p-5" data-testid="radmin-access-requests">
          <p className="text-[12px] font-bold text-gray-900 mb-3">
            🔑 {isEs ? `Solicitudes de acceso desde el login (${accessReqs.length})` : `Access requests from login (${accessReqs.length})`}
          </p>
          <div className="space-y-2">
            {accessReqs.slice((accessPage - 1) * 5, accessPage * 5).map((q) => (
              <div key={q.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-[12.5px]" data-testid={`radmin-access-req-${q.email}`}>
                <span className="font-bold text-gray-900">{q.name || q.email}</span>
                <span className="text-gray-500">{q.email}</span>
                {q.restaurant && <span className="rounded-full bg-white border border-sky-300 px-2.5 py-0.5 text-[11px] font-bold text-sky-700">{q.restaurant}</span>}
                <span className="ml-auto text-[11px] text-gray-400">{fmtDate(q.created_at)}</span>
              </div>
            ))}
          </div>
          <Pager page={accessPage} pages={Math.max(1, Math.ceil(accessReqs.length / 5))} setPage={setAccessPage} testid="radmin-access-pager" />
          <p className="mt-2 text-[11px] text-gray-400">{isEs ? 'Usa "Invitar restaurante" arriba para darles acceso con un clic.' : 'Use "Invite Restaurant" above to grant access in one click.'}</p>
        </Card>
      )}

      {(rows || []).filter((r) => r.slug === 'taqueria-de-ninas').map((r) => {
        const accent = r.branding?.primary_color || '#F77F11';
        return (
          <Card key={r.id} className="mt-4 p-0 overflow-hidden" data-testid="radmin-flagship-card">
            <div className="h-1.5" style={{ background: accent }} />
            <div className="p-5 flex flex-wrap items-center gap-5">
              {r.branding?.logo_url && <img src={r.branding.logo_url} alt={r.name} className="h-14 w-auto max-w-[160px] object-contain rounded-xl" />}
              <div className="min-w-[180px]">
                <div className="flex items-center gap-2">
                  <b className="text-[16px] text-gray-900">{r.name}</b>
                  <span className="rounded-full text-white text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5" style={{ background: accent }}>🔥 Flagship Demo</span>
                </div>
                <p className="text-[11.5px] text-gray-400 font-mono mt-0.5">{r.instance?.os_id} · {isEs ? 'producción' : 'production'}</p>
                <a href={r.branding?.social?.website || '#'} target="_blank" rel="noreferrer" className="text-[11.5px] font-semibold hover:underline" style={{ color: accent }}>{(r.branding?.social?.website || '').replace('https://', '')}</a>
              </div>
              <div className="flex gap-6 text-center">
                {[[r.orders_rows.toLocaleString(), isEs ? 'órdenes' : 'orders'], [r.imports, 'imports'], [`${r.active_users}/${r.users}`, isEs ? 'usuarios' : 'users']].map(([v, l]) => (
                  <div key={l}><p className="text-[20px] font-semibold text-gray-900 tabular-nums">{v}</p><p className="text-[10px] uppercase tracking-wider text-gray-400">{l}</p></div>
                ))}
              </div>
              <div className="ml-auto flex flex-col items-end gap-2">
                <div className="flex gap-2">
                  <button onClick={() => openUsers(r)} data-testid="radmin-flagship-users"
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-[11.5px] font-semibold hover:border-gray-400 transition-colors">
                    <Users className="w-3 h-3" /> {isEs ? 'Usuarios' : 'Users'}
                  </button>
                  <button onClick={() => { setInviteFor(r); setInvLink(''); }} data-testid="radmin-flagship-invite"
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-[11.5px] font-semibold hover:border-gray-400 transition-colors">
                    <Mail className="w-3 h-3" /> {isEs ? 'Invitar' : 'Invite'}
                  </button>
                  <button onClick={() => enter(r.id)} data-testid="radmin-flagship-enter"
                    className="inline-flex items-center gap-1.5 rounded-full text-white px-5 py-2 text-[12.5px] font-bold hover:opacity-90 transition-opacity shadow-md" style={{ background: accent }}>
                    <LogIn className="w-3.5 h-3.5" /> {isEs ? 'Entrar al OS del cliente' : 'Enter customer OS'}
                  </button>
                </div>
                <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/restaurant/login/${r.slug}`)} data-testid="radmin-flagship-copy-login"
                  className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors">
                  <Copy className="w-3 h-3" /> {isEs ? 'Copiar link de acceso privado' : 'Copy private access link'}
                </button>
              </div>
            </div>
          </Card>
        );
      })}

      <Card className="mt-4 overflow-hidden" data-testid="radmin-list">
        <table className="w-full">
          <thead><tr><Th>{isEs ? 'Instancia' : 'Instance'}</Th><Th>OS ID</Th><Th>{isEs ? 'Usuarios' : 'Users'}</Th><Th>{isEs ? 'Datos' : 'Data'}</Th><Th>{isEs ? 'Creado' : 'Created'}</Th><Th>{isEs ? 'Acciones' : 'Actions'}</Th></tr></thead>
          <tbody>
            {(rows || []).filter((r) => r.slug !== 'taqueria-de-ninas').map((r) => (
              <tr key={r.id} data-testid={`radmin-row-${r.slug}`}>
                <Td><b className="text-gray-900">{r.name}</b>{r.slug === 'makondo' && <span className="ml-1.5 text-amber-400" title="Demo preferida" data-testid="radmin-star-makondo">★</span>}<p className="text-[11px] text-gray-400">/restaurant/login/{r.slug}</p></Td>
                <Td className="font-mono text-[12px]">{r.instance?.os_id}</Td>
                <Td className="tabular-nums">{r.active_users}/{r.users}</Td>
                <Td className="tabular-nums">{r.orders_rows} rows · {r.imports} imports</Td>
                <Td>{fmtDate(r.created_at)}</Td>
                <Td>
                  <div className="flex gap-2">
                    <button onClick={() => openUsers(r)} data-testid={`radmin-users-${r.slug}`}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[11.5px] font-semibold hover:border-gray-400 transition-colors">
                      <Users className="w-3 h-3" /> {isEs ? 'Usuarios' : 'Users'}
                    </button>
                    <button onClick={() => { setInviteFor(r); setInvLink(''); }} data-testid={`radmin-invite-${r.slug}`}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[11.5px] font-semibold hover:border-[#F77F11] hover:text-[#F77F11] transition-colors">
                      <Mail className="w-3 h-3" /> {isEs ? 'Invitar' : 'Invite'}
                    </button>
                    <button onClick={() => enter(r.id)} data-testid={`radmin-enter-${r.slug}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#F77F11] text-white px-3 py-1 text-[11.5px] font-semibold hover:bg-[#d8651a] transition-colors">
                      <LogIn className="w-3 h-3" /> {isEs ? 'Entrar como cliente' : 'Enter as customer'}
                    </button>
                    {r.slug !== 'casa-mia' && (
                      <button onClick={() => removeInstance(r)} data-testid={`radmin-delete-${r.slug}`}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[11.5px] font-semibold text-gray-400 hover:border-red-400 hover:text-red-500 transition-colors">
                        <Trash2 className="w-3 h-3" /> {isEs ? 'Eliminar' : 'Delete'}
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
            {rows && !rows.filter((r) => r.slug !== 'taqueria-de-ninas').length && <tr><Td colSpan={6} className="text-center text-gray-400 py-8">{isEs ? 'Las próximas instancias de clientes aparecerán aquí.' : 'Upcoming customer instances will appear here.'}</Td></tr>}
          </tbody>
        </table>
      </Card>

      {usersFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setUsersFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="radmin-users-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-[16px] font-semibold">{isEs ? 'Usuarios de' : 'Users of'} {usersFor.name}</h3>
              <button onClick={() => setUsersFor(null)} className="text-gray-400 hover:text-gray-700 text-[18px] leading-none">✕</button>
            </div>
            {!usersList && <p className="mt-4 text-[13px] text-gray-400">…</p>}
            {usersList && usersList.map((u) => (
              <div key={u.id} className="mt-3 rounded-xl border border-gray-200 p-4" data-testid={`radmin-user-${u.email}`}>
                <div className="flex flex-wrap items-center gap-2.5">
                  <b className="text-[13.5px] text-gray-900">{u.name || u.email}</b>
                  <span className="text-[12px] text-gray-400">{u.email}</span>
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] uppercase font-bold text-gray-500">{u.role}</span>
                  <span className={`text-[11px] font-semibold ${u.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>{u.status === 'active' ? (isEs ? '● activo' : '● active') : (isEs ? '○ invitado' : '○ invited')}</span>
                  <span className="ml-auto text-[11px] text-gray-400">{isEs ? 'Último acceso:' : 'Last login:'} <b className="text-gray-600">{u.last_login ? fmtDate(u.last_login) : (isEs ? 'nunca' : 'never')}</b></span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => resetPassword(u)} data-testid={`radmin-reset-pwd-${u.email}`}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[11.5px] font-semibold hover:border-[#F77F11] hover:text-[#F77F11] transition-colors">
                    <KeyRound className="w-3 h-3" /> {isEs ? 'Resetear contraseña' : 'Reset password'}
                  </button>
                  <button onClick={() => diagnose(u)} data-testid={`radmin-diagnose-${u.email}`}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[11.5px] font-semibold hover:border-sky-500 hover:text-sky-600 transition-colors">
                    <Stethoscope className="w-3 h-3" /> {isEs ? 'Diagnosticar login' : 'Diagnose login'}
                  </button>
                </div>
              </div>
            ))}
            {resetResult && !resetResult.error && (
              <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="radmin-reset-result">
                <p className="text-[12px] font-bold text-emerald-700">{isEs ? 'Nueva contraseña generada — cópiala AHORA, no se volverá a mostrar:' : 'New password generated — copy it NOW, it will not be shown again:'}</p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="rounded bg-white border border-emerald-200 px-3 py-1.5 text-[14px] font-mono font-bold text-gray-900" data-testid="radmin-new-password">{resetResult.new_password}</code>
                  <button onClick={() => navigator.clipboard.writeText(resetResult.new_password)} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700 hover:underline">
                    <Copy className="w-3 h-3" /> {isEs ? 'Copiar' : 'Copy'}
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-emerald-700/80">{resetResult.email}</p>
              </div>
            )}
            {resetResult?.error && <p className="mt-3 text-[12px] text-red-600">✕ {resetResult.error}</p>}
            {diag && !diag.loading && (
              <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="radmin-diag-result">
                <p className="text-[12px] font-bold text-sky-700 mb-2">{isEs ? 'Diagnóstico de acceso' : 'Login diagnostic'} · DB: <span className="font-mono">{diag.environment_db}</span></p>
                <div className="flex flex-wrap gap-2 text-[11.5px]">
                  {[[isEs ? 'Usuario existe' : 'User exists', diag.user_exists], [isEs ? 'Cuenta activa' : 'Account active', diag.account_active],
                    [isEs ? 'Contraseña definida' : 'Password set', diag.has_password], [isEs ? 'Restaurante asignado' : 'Restaurant assigned', !!diag.restaurant_assigned]].map(([k, v]) => (
                      <span key={k} className={`rounded-full px-2.5 py-0.5 font-semibold ${v ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{v ? '✓' : '✕'} {k}</span>
                    ))}
                  {diag.restaurant_assigned && <span className="rounded-full bg-white border border-sky-200 px-2.5 py-0.5 text-sky-700">{diag.restaurant_assigned.name}</span>}
                </div>
                <ul className="mt-2.5 space-y-1">
                  {(diag.reasons || []).map((x, i) => <li key={i} className="text-[12px] text-sky-800">• {x}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {inviteFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setInviteFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="radmin-invite-modal">
            <h3 className="text-[16px] font-semibold">{isEs ? 'Invitar a' : 'Invite to'} {inviteFor.name}</h3>
            <form onSubmit={invite} className="mt-4 space-y-3">
              <input type="email" required value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="Email" data-testid="radmin-invite-email"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px]" />
              <input value={invName} onChange={(e) => setInvName(e.target.value)} placeholder={isEs ? 'Nombre (opcional)' : 'Name (optional)'} data-testid="radmin-invite-name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px]" />
              <button data-testid="radmin-invite-send" className="w-full rounded-lg bg-gray-900 hover:bg-[#F77F11] text-white font-semibold text-[13px] py-2.5 transition-colors">
                {isEs ? 'Generar invitación' : 'Generate invitation'}
              </button>
            </form>
            {invLink && (
              <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3" data-testid="radmin-invite-link">
                <p className="text-[11px] text-gray-500 mb-1">{isEs ? 'Enlace de invitación (7 días, un solo uso):' : 'Invitation link (7 days, single use):'}</p>
                <p className="text-[11.5px] font-mono break-all text-gray-800">{invLink}</p>
                <button onClick={() => navigator.clipboard.writeText(invLink)} className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#F77F11]">
                  <Copy className="w-3 h-3" /> {isEs ? 'Copiar' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
